import { AuthService } from './../../../shared/services/auth.service';
import { SitesService } from './../../../shared/services/sites.service';
import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormBuilder, Validators, FormArray, FormControl, AbstractControl, FormGroup } from '@angular/forms';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UiService } from '@services/ui.service';
import { ClientsService } from '@services/clients.service';
import { Client } from '@classes/client';
import { RoleName } from '@enums/role-name.enum';
import { FeaturesService } from '@services/features.service';
import { Feature } from '@classes/feature';

@Component({
  selector: 'app-client-edit',
  templateUrl: './client-edit.component.html',
  styleUrls: ['./client-edit.component.scss']
})
export class ClientEditComponent extends FormEditBaseComponent implements OnInit {
  @Input() client: Client;

  fiftyMaxLength = 50;
  twoHundredsFiftyMaxLength = 250;
  fourTeenMaxLength = 14;
  fiveMaxLength = 5;
  thirteenMaxLength = 13;
  isAdmin: boolean;
  allFeatures: Feature[] = [];
  selectableFeatures: Feature[] = [];
  featuresFormArray: FormArray;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private clientsSrv: ClientsService,
    private sitesSrv: SitesService,
    private authSrv: AuthService,
    private featuresSrv: FeaturesService
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.isAdmin = this.authSrv.getUser().role.name === RoleName.admin;
    this.initSubscriptions();
  }

  onSubmit(): void {
    this.isLoading = true;
    if (this.client) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  private update(): void {
    if (this.isAdmin) {
      let object = {
        email: null,
        phone: null,
        fax: null,
        contact: null,
        webSite: null,
        street: null,
        streetNumber: null,
        locality: null,
        postalCode: null,
        siret: null,
        ape: null,
        tva: null
      };
      object.email = this.editForm.value.email;
      object.phone = this.editForm.value.phone;
      object.fax = this.editForm.value.fax;
      object.contact = this.editForm.value.contact;
      object.webSite = this.editForm.value.webSite;
      object.street = this.editForm.value.street;
      object.streetNumber = this.editForm.value.streetNumber;
      object.locality = this.editForm.value.locality;
      object.postalCode = this.editForm.value.postalCode;
      object.siret = this.editForm.value.siret;
      object.ape = this.editForm.value.ape;
      object.tva = this.editForm.value.tva;

      this.clientsSrv.putAdminClient(object, this.client.id).subscribe(() => this.isLoading = false);
    } else {
      const selectedFeaturesId = this.editForm.value.features
        .map((checked, i) => checked ? this.selectableFeatures[i].id : null)
        .filter(v => v !== null);

      const clientCopy: Client = JSON.parse(JSON.stringify(this.client));
      clientCopy.name = this.editForm.value.name;
      clientCopy.email = this.editForm.value.email;
      clientCopy.phone = this.editForm.value.phone;
      clientCopy.fax = this.editForm.value.fax;
      clientCopy.contact = this.editForm.value.contact;
      clientCopy.webSite = this.editForm.value.webSite;
      clientCopy.street = this.editForm.value.street;
      clientCopy.streetNumber = this.editForm.value.streetNumber;
      clientCopy.locality = this.editForm.value.locality;
      clientCopy.postalCode = this.editForm.value.postalCode;
      clientCopy.siret = this.editForm.value.siret;
      clientCopy.ape = this.editForm.value.ape;
      clientCopy.tva = this.editForm.value.tva;

      this.clientsSrv.put(clientCopy).subscribe((clientResponse) => {
        if (selectedFeaturesId.length > 0 && !this.editForm.get('features').pristine) {
          selectedFeaturesId.push(this.allFeatures.find(feature => feature.name === 'Véhicule').id);

          this.featuresSrv.putClientFeatures(clientResponse.id, selectedFeaturesId).subscribe((client) => {
            this.clientsSrv.updateClientsSubject(client);
            this.isLoading = false;
          })
        } else if (selectedFeaturesId.length === 0 && !this.editForm.get('features').pristine) {
          this.featuresSrv.putClientFeatures(clientResponse.id, selectedFeaturesId).subscribe((client) => {
            this.clientsSrv.updateClientsSubject(client);
            this.isLoading = false;
          })
        } else {
          this.isLoading = false;
        }
      });
    }

  }

  private create(): void {
    this.client = new Client(
      this.editForm.value.name,
      this.editForm.value.email,
      this.editForm.value.phone,
      this.editForm.value.fax,
      this.editForm.value.contact,
      this.editForm.value.webSite,
      this.editForm.value.street,
      this.editForm.value.streetNumber,
      this.editForm.value.locality,
      this.editForm.value.postalCode,
      this.editForm.value.siret,
      this.editForm.value.ape,
      this.editForm.value.tva
    );

    const selectedFeaturesId = this.editForm.value.features
      .map((checked, i) => checked ? this.selectableFeatures[i].id : null)
      .filter(v => v !== null);

    this.clientsSrv.post(this.client)
      .subscribe(
        (clientResponse) => {
          if (selectedFeaturesId.length > 0 && !this.isAdmin) {
            selectedFeaturesId.push(this.allFeatures.find(feature => feature.name === 'Véhicule').id);
            this.featuresSrv.putClientFeatures(clientResponse.id, selectedFeaturesId).subscribe((client) => {
              this.clientsSrv.updateClientsSubject(client);
              this.isLoading = false;
            })
          } else {
            this.isLoading = false;
          }
        },
        () => this.client = null
      );
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    this.addSubscription(
      this.featuresSrv.get().subscribe(featuresFromSrv => {
        this.allFeatures = featuresFromSrv;

        this.initForm();
        this.isLoading = false;
      })
    )
  }

  private initForm(): void {
    let name = '';
    let email = '';
    let phone = '';
    let fax = '';
    let contact = '';
    let webSite = '';
    let street = '';
    let streetNumber = '';
    let locality = '';
    let postalCode = '';
    let siret = '';
    let ape = '';
    let tva = '';
    let features = [];

    if (this.client) {
      name = this.client.name;
      email = this.client.email;
      phone = this.client.phone;
      fax = this.client.fax;
      contact = this.client.contact;
      webSite = this.client.webSite;
      street = this.client.street;
      streetNumber = this.client.streetNumber;
      locality = this.client.locality;
      postalCode = this.client.postalCode;
      siret = this.client.siret;
      ape = this.client.ape;
      tva = this.client.tva;
      features = this.client.features;
    }

    this.editForm = this.fb.group({
      name: [name, [Validators.required, Validators.maxLength(this.fiftyMaxLength)]],
      email: [email],
      phone: [phone, Validators.pattern("^[+0-9]*$")],
      fax: [fax],
      contact: [contact],
      webSite: [webSite, [Validators.maxLength(this.fiftyMaxLength)]],
      street: [street, [Validators.maxLength(this.twoHundredsFiftyMaxLength)]],
      streetNumber: [streetNumber, [Validators.maxLength(this.fiftyMaxLength)]],
      locality: [locality, [Validators.maxLength(this.fiftyMaxLength)]],
      postalCode: [postalCode, [Validators.maxLength(this.fiftyMaxLength)]],
      siret: [siret, [Validators.maxLength(this.fourTeenMaxLength)]],
      ape: [ape, [Validators.maxLength(this.fiveMaxLength)]],
      tva: [tva, [Validators.maxLength(this.thirteenMaxLength)]],
      features: this.fb.array([])
    });

    this.addFeatures();

    if (features.length > 0) {
      this.selectableFeatures.map((feature, index) => {
        if (features.find(f => f.id === feature.id)) {
          let formArrayFeatures = this.editForm.get('features') as FormArray;
          formArrayFeatures.controls[index].setValue(true)
        }
      })
    }
  }

  addFeatures(): void {
    this.featuresFormArray = this.editForm.get('features') as FormArray;

    this.selectableFeatures = this.allFeatures.filter(feature => feature.name !== 'Véhicule');

    this.selectableFeatures.forEach(() => this.featuresFormArray.push(new FormControl(false)));
  }
}

